// @ts-nocheck
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchAllMarkets, formatCurrencyValue } from '../../services/marketService';
import { getSubdomain, getAppsUrl } from '../../utils/subdomain';
import './LiveMarketBanner.css';

const STORAGE_KEY = 'live_market_banner_visible';
const DATA_CACHE_KEY = 'live_market_data_cache';

// Fallback seed data in case API is offline
const FALLBACK_MARKET_DATA = {
  commodities: [
    { symbol: 'GOLD', name: 'Gold', price: 2654.8, change: 12.4, changePercent: 0.5, currency: 'USD', flag: '🪙' },
    { symbol: 'SILVER', name: 'Silver', price: 31.45, change: -0.25, changePercent: -0.8, currency: 'USD', flag: '🥈' },
    { symbol: 'BRENT', name: 'Brent Crude', price: 74.2, change: 1.15, changePercent: 1.6, currency: 'USD', flag: '🛢️' },
    { symbol: 'COPPER', name: 'Copper', price: 4.38, change: 0.04, changePercent: 0.9, currency: 'USD', flag: '🧱' }
  ],
  indices: [
    { symbol: 'NIFTY50', name: 'Nifty 50', price: 24360.5, change: 145.2, changePercent: 0.6, currency: 'INR', flag: '🇮🇳' },
    { symbol: 'SENSEX', name: 'BSE Sensex', price: 79820.1, change: 480.0, changePercent: 0.6, currency: 'INR', flag: '🇮🇳' },
    { symbol: 'SPX', name: 'S&P 500', price: 5860.2, change: -18.4, changePercent: -0.3, currency: 'USD', flag: '🇺🇸' },
    { symbol: 'NASDAQ', name: 'Nasdaq 100', price: 20380.0, change: 85.0, changePercent: 0.4, currency: 'USD', flag: '🇺🇸' }
  ],
  forex: [
    { symbol: 'USD/INR', name: 'USD to INR', price: 84.12, change: 0.08, changePercent: 0.1, currency: 'INR', flag: '💵' },
    { symbol: 'EUR/USD', name: 'EUR to USD', price: 1.082, change: -0.003, changePercent: -0.3, currency: 'USD', flag: '💶' },
    { symbol: 'GBP/USD', name: 'GBP to USD', price: 1.298, change: 0.004, changePercent: 0.3, currency: 'USD', flag: '💷' },
    { symbol: 'USD/JPY', name: 'USD to JPY', price: 152.6, change: 0.45, changePercent: 0.3, currency: 'JPY', flag: '💴' }
  ],
  crypto: [
    { symbol: 'BTC', name: 'Bitcoin', price: 68450.0, change: 1250.0, changePercent: 1.9, currency: 'USD', flag: '₿' },
    { symbol: 'ETH', name: 'Ethereum', price: 2540.0, change: -32.0, changePercent: -1.2, currency: 'USD', flag: '⟠' },
    { symbol: 'SOL', name: 'Solana', price: 178.5, change: 6.8, changePercent: 4.0, currency: 'USD', flag: '◎' }
  ]
};

// Initial state loader for 0ms cached data
const getInitialState = () => {
  try {
    const cached = localStorage.getItem(DATA_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && parsed.data && (parsed.data.commodities || parsed.data.indices)) {
        return {
          data: parsed.data,
          lastUpdated: parsed.lastUpdated || new Date().toISOString(),
          isLoading: false,
        };
      }
    }
  } catch {}
  return {
    data: null,
    lastUpdated: null,
    isLoading: true,
  };
};

export default function LiveMarketBanner() {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored !== 'false';
    } catch {
      return true;
    }
  });

  const initialState = useMemo(() => getInitialState(), []);
  const [marketData, setMarketData] = useState(initialState.data);
  const [lastUpdated, setLastUpdated] = useState(initialState.lastUpdated);
  const [isLoading, setIsLoading] = useState(initialState.isLoading);
  const isMountedRef = useRef(true);

  // Sync visibility state from custom events / storage
  useEffect(() => {
    const handleVisibilityUpdate = (e) => {
      if (e && e.detail !== undefined) {
        setIsVisible(e.detail);
      } else {
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          setIsVisible(stored !== 'false');
        } catch {}
      }
    };

    window.addEventListener('liveMarketBannerVisibilityChanged', handleVisibilityUpdate);
    window.addEventListener('storage', handleVisibilityUpdate);

    return () => {
      window.removeEventListener('liveMarketBannerVisibilityChanged', handleVisibilityUpdate);
      window.removeEventListener('storage', handleVisibilityUpdate);
    };
  }, []);

  // Quick background data fetch with seamless caching
  useEffect(() => {
    isMountedRef.current = true;

    const loadData = async () => {
      try {
        const response = await fetchAllMarkets();
        if (response && response.success && response.data && isMountedRef.current) {
          const updatedTime = response.lastUpdated || new Date().toISOString();
          setMarketData(response.data);
          setLastUpdated(updatedTime);
          setIsLoading(false);

          try {
            localStorage.setItem(
              DATA_CACHE_KEY,
              JSON.stringify({ data: response.data, lastUpdated: updatedTime })
            );
          } catch {}
        }
      } catch (err) {
        if (isMountedRef.current && !marketData) {
          setMarketData(FALLBACK_MARKET_DATA);
          setIsLoading(false);
        }
      }
    };

    loadData();

    // Poll every 60 seconds for fresh market ticks
    const interval = setInterval(loadData, 60000);
    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
    };
  }, []);

  // Determine if banner should be displayed on current page
  const shouldDisplay = useMemo(() => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const subdomain = getSubdomain(hostname);
    const path = location.pathname.toLowerCase();

    // 1. Blog Pages: blog subdomain or /blog*, /blogpage*, /category/*
    if (subdomain === 'blog') return true;
    if (path.startsWith('/blog') || path.startsWith('/blogpage') || path.startsWith('/category')) return true;

    // 2. Podcast Pages: podcast subdomain or /podcast*
    if (subdomain === 'podcast') return true;
    if (path.startsWith('/podcast')) return true;

    // 3. Store Pages: store subdomain or /store*, /cart, /checkout, /order-history, /order-success
    if (subdomain === 'store') return true;
    if (
      path.startsWith('/store') ||
      path.startsWith('/cart') ||
      path.startsWith('/checkout') ||
      path.startsWith('/order-history') ||
      path.startsWith('/order-success')
    ) {
      return true;
    }

    // 4. AppStore Homepage: apps subdomain at '/' or '/app-store' on main domain
    if (subdomain === 'apps' && (path === '/' || path === '' || path === '/app-store' || path === '/app-store/')) {
      return true;
    }
    if (path === '/app-store' || path === '/app-store/') {
      return true;
    }

    // 5. Main Homepage: root /
    if (subdomain === '' && (path === '/' || path === '')) {
      return true;
    }

    // Hide on all other sub-apps (e.g. /pomodoro, /market, /learnwithai, /signin, /admin, etc.)
    return false;
  }, [location.pathname]);

  const handleCloseClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Open Sidenav
    if (typeof window.openSideNav === 'function') {
      window.openSideNav();
    } else {
      const sideNav = document.getElementById('mySidenav');
      const hamBtn = document.getElementById('ham_btn');
      if (sideNav) {
        const wid = window.innerWidth || window.screen.availWidth;
        sideNav.style.width = wid <= 480 ? '100%' : '350px';
      }
      if (hamBtn) hamBtn.style.visibility = 'hidden';
      document.body.classList.add('sidenav-open');
    }

    // Dispatch event to open Settings in Sidenav and highlight the Live Market Banner toggle
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('highlightMarketBannerToggle'));
    }, 150);
  };

  if (!isVisible || !shouldDisplay) {
    return null;
  }

  const marketAppUrl = getAppsUrl('/market');

  const categories = marketData
    ? [
        { key: 'commodities', label: 'COMMODITIES', icon: '🪙', items: marketData.commodities || [] },
        { key: 'indices', label: 'INDICES', icon: '📈', items: marketData.indices || [] },
        { key: 'forex', label: 'FOREX', icon: '💱', items: marketData.forex || [] },
        { key: 'crypto', label: 'CRYPTO', icon: '⚡', items: marketData.crypto || [] },
      ]
    : [];

  // Helper to render one set of all category items
  const renderItemsSet = (keyPrefix: string) => {
    return categories.map((cat) => {
      if (!cat.items || cat.items.length === 0) return null;

      return (
        <div key={`${keyPrefix}-${cat.key}`} className="live-market-category-group">
          {/* Category Label Badge */}
          <span className="live-market-category-badge">
            <span>{cat.icon}</span>
            <span>{cat.label}</span>
          </span>

          {/* Asset Items in this Category */}
          {cat.items.map((item, itemIdx) => {
            const changeVal = item.changePercent !== undefined ? item.changePercent : item.change || 0;
            const isPos = changeVal > 0;
            const isNeg = changeVal < 0;
            const changeClass = isPos ? 'positive' : isNeg ? 'negative' : 'neutral';
            const arrow = isPos ? '▲' : isNeg ? '▼' : '';
            const formattedChange = `${isPos ? '+' : ''}${Number(changeVal).toFixed(1)}%`;

            return (
              <a
                key={`${keyPrefix}-${cat.key}-${item.symbol || itemIdx}`}
                href={marketAppUrl}
                className="live-market-item"
                title={`${item.name || item.symbol}: ${formatCurrencyValue(item.price, item.currency)} (${formattedChange}) - Click to view in Market App`}
              >
                <div className="live-market-item-top">
                  <span className="live-market-item-title">{item.name || item.symbol}</span>
                </div>
                <div className="live-market-item-bottom">
                  <span className="live-market-price live-market-font">
                    {formatCurrencyValue(item.price, item.currency)}
                  </span>
                  <span className={`live-market-change live-market-font ${changeClass}`}>
                    {arrow} {formattedChange}
                  </span>
                </div>
              </a>
            );
          })}

          {/* Vertical Small Dull Divider line between category-wise data */}
          <div className="live-market-vertical-divider" aria-hidden="true" />
        </div>
      );
    });
  };

  const formatLastUpdatedTime = (isoStr: string) => {
    if (!isoStr) return '';
    try {
      const d = new Date(isoStr);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <div className="live-market-banner-wrapper" role="region" aria-label="Live Market Banner">
      {/* Scrolling Ticker Area */}
      <div className="live-market-scroll-area">
        {isLoading || !marketData ? (
          <div className="live-market-skeleton-track" aria-hidden="true">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((idx) => (
              <React.Fragment key={`skel-${idx}`}>
                {idx % 3 === 1 && (
                  <div className="live-market-skeleton-pill live-market-skeleton-badge" />
                )}
                <div className="live-market-skeleton-item">
                  <div className="live-market-skeleton-pill live-market-skeleton-title" />
                  <div className="live-market-skeleton-row">
                    <div className="live-market-skeleton-pill live-market-skeleton-price" />
                    <div className="live-market-skeleton-pill live-market-skeleton-change" />
                  </div>
                </div>
                <div className="live-market-vertical-divider" />
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="live-market-marquee-track">
            {/* Original set */}
            {renderItemsSet('track-1')}
            {/* Duplicate set for seamless continuous marquee loop */}
            {renderItemsSet('track-2')}
          </div>
        )}
      </div>

      {/* Right Static Market Live Badge */}
      <div className="live-market-badge-container">
        <a href={marketAppUrl} className="live-market-badge-text-group" title="Open Market App">
          <span className="live-market-title-big">MARKET</span>
          <span className="live-market-live-indicator">
            <span className="live-market-dot" />
            <span>LIVE</span>
          </span>
          {lastUpdated && (
            <span className="live-market-updated-time">
              {formatLastUpdatedTime(lastUpdated)}
            </span>
          )}
        </a>

        {/* Close Button that opens Sidenav to manage toggle */}
        <button
          type="button"
          className="live-market-close-btn"
          onClick={handleCloseClick}
          aria-label="Hide Live Market Banner"
          title="Manage banner in Menu"
        >
          <i className="fi fi-rr-cross-small" />
        </button>
      </div>
    </div>
  );
}
